import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BrandingContext = createContext(null);

const DEFAULT = {
  product_name: "Videra PM",
  logo_url: null,
  favicon_url: null,
  primary_color: "#1F4E79",
  secondary_color: "#0F172A",
  accent_color: "#E07A2F",
  org_name: "Default Workspace",
  sector: null,
  terminology: {},
  module_visibility: {},
  rag_area_labels: {},
};

const PLURALS = { Programme: "Programmes", Portfolio: "Portfolios", Project: "Projects" };

const buildTranslator = (terminology) => {
  const map = terminology || {};
  const entries = Object.entries(map).filter(([, v]) => v);
  if (entries.length === 0) return (s) => s;
  const pairs = [];
  entries.forEach(([from, to]) => {
    pairs.push([from, to]);
    const pf = PLURALS[from] || (from.endsWith("s") ? null : `${from}s`);
    if (pf) pairs.push([pf, PLURALS[to] || (to.endsWith("s") ? to : `${to}s`)]);
  });
  pairs.sort((a, b) => b[0].length - a[0].length);
  return (text) => {
    if (typeof text !== "string" || !text) return text;
    let out = text;
    pairs.forEach(([from, to]) => {
      if (from === to) return;
      out = out.replace(new RegExp(`\\b${from}\\b`, "g"), to);
      out = out.replace(new RegExp(`\\b${from.toLowerCase()}\\b`, "g"), to.toLowerCase());
    });
    return out;
  };
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT);
  const { user } = useAuth();

  const applyTheme = useCallback((b) => {
    const r = document.documentElement;
    r.style.setProperty("--brand-primary", b.primary_color);
    r.style.setProperty("--brand-secondary", b.secondary_color);
    r.style.setProperty("--brand-accent", b.accent_color);
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await api.get("/branding");
    setBranding({ ...DEFAULT, ...data });
    applyTheme(data);
    document.title = data.product_name || "Videra PM";
    let link = document.querySelector("link[rel='icon']");
    if (data.favicon_url) {
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = data.favicon_url.startsWith("/api/")
        ? `${process.env.REACT_APP_BACKEND_URL}${data.favicon_url}`
        : data.favicon_url;
    } else if (link) {
      link.href = "/favicon.ico";
    }
  }, [applyTheme]);

  // Branding is tenant-scoped server-side, so it must be refetched whenever the signed-in
  // identity changes — otherwise the anonymous defaults fetched before login would persist.
  useEffect(() => {
    refresh().catch(() => applyTheme(DEFAULT));
  }, [refresh, applyTheme, user?.org_id]);

  const t = useMemo(() => buildTranslator(branding.terminology), [branding.terminology]);
  const moduleOn = useCallback(
    (key) => branding.module_visibility?.[key] !== false,
    [branding.module_visibility],
  );

  return (
    <BrandingContext.Provider value={{ branding, setBranding, refresh, t, moduleOn }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
